import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { Profile, ProfileRecommendation, ProfileRecommendationField } from '@/types/api'

import { missingChips, type MissingItem } from './completeness'
import { PlusIcon, tabHref, useIsPhone } from './shared'

/** Where each recommendation's "Add" lands: an input on the Profile tab, or a tab. */
const JUMP: Partial<Record<ProfileRecommendationField, { href: string; verb: 'Add' | 'Show' }>> = {
  current_salary: { href: tabHref('profile', 'profile-edit-currentSalary'), verb: 'Add' },
  ctc: { href: tabHref('profile', 'profile-edit-currentSalary'), verb: 'Add' },
  expected_salary: { href: tabHref('profile', 'profile-edit-expectedSalary'), verb: 'Add' },
  notice_period: { href: tabHref('profile', 'profile-edit-noticePeriodDays'), verb: 'Add' },
  current_title: { href: tabHref('profile', 'profile-edit-currentTitle'), verb: 'Add' },
  current_company: { href: tabHref('profile', 'profile-edit-currentCompany'), verb: 'Add' },
  linkedin: { href: tabHref('profile', 'profile-edit-linkedinUrl'), verb: 'Add' },
  additional_notes: { href: tabHref('profile', 'profile-edit-note-cloud'), verb: 'Add' },
  skills: { href: tabHref('skills'), verb: 'Show' },
  skill_years: { href: tabHref('skills'), verb: 'Show' },
}

/**
 * "N details to add": what applications asked for and the profile could not
 * answer, then everything else still missing. One collapsible panel, open on
 * a desktop and folded on a phone so the form below is one swipe away.
 */
export function Recommendations({
  items,
  profile,
  missing = [],
}: {
  items: ProfileRecommendation[]
  profile: Profile
  /** From `profileCompleteness`: the quick "Add X" chips. */
  missing?: MissingItem[]
}) {
  const navigate = useNavigate()
  const phone = useIsPhone()
  const [open, setOpen] = useState(!phone)
  useEffect(() => setOpen(!phone), [phone])

  // Chips for what the recommendations above do not already cover.
  const chips = missingChips(missing, items)
  const count = items.length + chips.length
  if (count === 0) return null

  return (
    <section className="card overflow-hidden" aria-labelledby="profile-todo-title">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="profile-todo"
        className="flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted/60 sm:px-5"
      >
        <span className="num grid h-7 min-w-[28px] shrink-0 place-items-center rounded-pill bg-warning/14 px-2 text-[13px] font-semibold text-warning">
          {count}
        </span>
        <span className="min-w-0 flex-1">
          <span id="profile-todo-title" className="block text-[15px] font-semibold text-ink">
            {count === 1 ? '1 detail to add' : `${count} details to add`}
          </span>
          <span className="block truncate text-[12.5px] text-ink-muted">
            Add these once and the screening questions behind them answer themselves.
          </span>
        </span>
        <Chevron open={open} />
      </button>

      {open && (
        <div id="profile-todo" className="border-t border-line">
          {items.length > 0 && (
            <ul className="divide-y divide-line">
              {items.map((rec) => {
                const jump = JUMP[rec.field]
                return (
                  <li
                    key={rec.id}
                    className="flex flex-col gap-2.5 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        <p className="text-[14.5px] font-medium text-ink">{rec.title}</p>
                        {rec.askedCount > 0 && (
                          <StatusPill tone="warning" size="sm">
                            Asked in <span className="num">{rec.askedCount}</span>{' '}
                            {rec.askedCount === 1 ? 'application' : 'applications'}
                          </StatusPill>
                        )}
                      </div>
                      {rec.detail && (
                        <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft">{rec.detail}</p>
                      )}
                      {rec.examples?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {rec.examples.slice(0, 2).map((example) => (
                            <li
                              key={example}
                              className="line-clamp-2 break-words text-[12.5px] leading-relaxed text-ink-faint"
                            >
                              “{example}”
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="shrink-0">
                      {rec.field === 'skill_years' && rec.skill ? (
                        <SkillYearsForm skill={rec.skill} profile={profile} />
                      ) : jump ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(jump.href)}
                          className="w-full sm:w-auto"
                        >
                          {jump.verb}
                        </Button>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {chips.length > 0 && (
            <ul
              className={cn(
                'flex flex-wrap gap-1.5 px-4 py-3.5 sm:px-5',
                items.length > 0 && 'border-t border-line bg-surface-muted/40',
              )}
            >
              {chips.map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={() => navigate(tabHref(item.tab, item.anchor))}
                    className="inline-flex h-9 items-center gap-1 rounded-pill border border-line bg-surface px-3 text-[12.5px] font-medium text-ink-soft transition-colors hover:border-brand hover:bg-brand-soft hover:text-ink"
                  >
                    <PlusIcon className="h-3 w-3 text-brand" />
                    Add {item.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

/** Years for one skill, saved inline: no need to open the Skills tab. */
export function SkillYearsForm({ skill, profile }: { skill: string; profile: Profile }) {
  const queryClient = useQueryClient()
  const existing = profile.skills.find((s) => s.name.toLowerCase() === skill.toLowerCase())
  const [years, setYears] = useState(existing?.yearsOfExperience?.toString() ?? '')
  const save = useMutation({
    mutationFn: () =>
      profileApi.updateSkills([
        {
          name: existing?.name ?? skill,
          yearsOfExperience: Number(years),
          isPrimary: existing?.isPrimary ?? false,
        },
      ]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profile-recommendations'] })
      toast.success('Skill updated', `${skill}: ${years} years`)
    },
    onError: (error: ApiError) => toast.error('Could not save', error.message),
  })
  const valid = years !== '' && Number(years) >= 0 && Number(years) <= 60
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (valid) save.mutate()
      }}
    >
      <label className="sr-only" htmlFor={`years-${skill}`}>
        Years of {skill}
      </label>
      <input
        id={`years-${skill}`}
        type="number"
        min={0}
        max={60}
        step={0.5}
        inputMode="decimal"
        placeholder="Years"
        value={years}
        onChange={(event) => setYears(event.target.value)}
        className="field-box num h-10 w-[96px] px-3 py-0 text-[14px] sm:h-9"
      />
      <Button size="sm" variant="secondary" type="submit" disabled={!valid} loading={save.isPending}>
        Add
      </Button>
    </form>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform', open && 'rotate-180')}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
